import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { GameStore } from '../stores/game-store';
import { CurrentGamesStore } from '../stores/current-games-store';
import './current-games.css';

export function CurrentGamesPanel({ game }: { readonly game: GameStore }) {
  const [store] = useState(() => new CurrentGamesStore(game));
  const state = useSyncExternalStore(store.subscribe, store.snapshot);
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    void store.load();
  }, [store]);
  useEffect(() => {
    const dialog = panel.current?.closest<HTMLElement>('[role="dialog"]');
    if (!dialog) return;
    const previous = document.activeElement;
    dialog.querySelector<HTMLButtonElement>('button')?.focus();
    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const buttons = [...dialog.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')];
      const first = buttons[0];
      const last = buttons.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    dialog.addEventListener('keydown', trapFocus);
    return () => {
      dialog.removeEventListener('keydown', trapFocus);
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);
  return (
    <div ref={panel} className="current-games" aria-busy={state.pending}>
      <div className="current-games__intro">
        <p>选择对局即可进入；退出或断线后也可在这里恢复。</p>
        <button
          className="secondary-button"
          type="button"
          disabled={state.pending || state.openingGameId !== null}
          onClick={() => void store.load()}
        >
          刷新列表
        </button>
      </div>
      {state.error ? (
        <p className="message message--error" role="alert">
          {state.error}
        </p>
      ) : null}
      {state.pending && state.games.length === 0 ? <p role="status">正在加载对局…</p> : null}
      {!state.pending && !state.error && state.games.length === 0 ? (
        <p role="status">暂无等待准备或进行中的对局。创建新对局，或使用房间码加入。</p>
      ) : null}
      <ul className="current-games__list">
        {state.games.map((item) => (
          <li key={item.gameId}>
            <button
              type="button"
              disabled={state.openingGameId !== null}
              onClick={() => void store.enter(item.gameId)}
            >
              <span className="current-games__names">
                <strong>
                  {item.players.map((player) => player.displayName).join(' · ')}
                  {item.players.length === 1 ? ' · 等待对手' : ''}
                </strong>
                <small>
                  {item.boardSize} 路 · 房间码 {item.roomCode}
                </small>
              </span>
              <span className="current-games__status">
                {['CREATED', 'WAITING', 'READY'].includes(item.status) ? '等待准备' : '进行中'}
              </span>
              <span>{state.openingGameId === item.gameId ? '正在进入…' : '进入对局'}</span>
            </button>
          </li>
        ))}
      </ul>
      {state.nextCursor ? (
        <button
          className="secondary-button secondary-button--wide"
          type="button"
          disabled={state.pending || state.openingGameId !== null}
          onClick={() => void store.load(true)}
        >
          {state.pending ? '正在加载…' : '加载更多对局'}
        </button>
      ) : null}
    </div>
  );
}
